import { SOURCE_LABELS } from '@netcon-work/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { zusammenfuehrenVorschauQuery, type Benutzer } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { Button } from './ui/primitives'
import { hintergrundSchliesst } from './ui/hintergrund'

/**
 * Personen zu **einer** zusammenführen (F-14, Vorarbeit für AP 3.5).
 *
 * Von Nils gefordert: „Wir haben Konten aus GitLab, aus NetCon und in Work. Die
 * sollte man kombinieren können, dass es EIN User ist."
 *
 * ## Warum hier eine Vorschau steht und keine bloße Rückfrage
 *
 * Der Vorgang ist **nicht rückgängig zu machen**: Ereignisse und Zuweisungen
 * werden umgehängt, die überzähligen Personen gelöscht. Ein „Sind Sie sicher?"
 * hilft dabei nichts – wer bestätigt, muss vorher gesehen haben, *was* umzieht.
 * Deshalb fragt dieser Dialog den Server, was passieren würde, und zeigt es an.
 *
 * Die Vorschau ruft dieselbe Prüfung wie der Vollzug (`zusammenfuehrenPruefen`).
 * Sonst könnte sie „geht" zeigen und der Klick scheitern.
 */
export function PersonenZusammenfuehren({
  ziel,
  quellen,
  schliessen,
  fertig,
}: {
  ziel: Benutzer
  quellen: Benutzer[]
  schliessen: () => void
  fertig: (text: string) => void
}) {
  const quellenIds = quellen.map((q) => q.id)
  const {
    data: vorschau,
    isLoading,
    error,
  } = useQuery(zusammenfuehrenVorschauQuery(ziel.id, quellenIds))

  const merge = useMutation({
    mutationFn: () => trpc.people.zusammenfuehren.mutate({ zielId: ziel.id, quellenIds }),
    onSuccess: (r) =>
      fertig(
        `${r.displayName}: ${r.zusammengefuehrt === 1 ? '1 Person' : `${r.zusammengefuehrt} Personen`} zusammengeführt – ` +
          `${r.ereignisse} Ereignisse und ${r.aufgaben} Aufgaben umgehängt.`,
      ),
  })

  const meldung = merge.error?.message ?? (error as Error | null)?.message ?? null

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[8vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label="Personen zusammenführen"
    >
      <div className="flex max-h-[min(90vh,100%)] sm:max-h-[min(80vh,100%)] w-full min-w-0 max-w-[560px] flex-col overflow-y-auto rounded-card border border-line bg-surface p-[18px] shadow-card">
        <h3 className="text-sm font-bold">Zu einer Person zusammenführen</h3>

        {isLoading && <p className="mt-2 text-[13px] text-ink2">Prüft …</p>}

        {meldung && (
          <p className="mt-2.5 rounded-lg bg-[#fdeaea] px-2.5 py-2 text-[12.5px] text-[#b02a2a]">
            {meldung}
          </p>
        )}

        {vorschau && (
          <>
            <p className="mt-2 text-[12.5px] text-ink2">
              Alles zieht zu <strong>{vorschau.ziel.displayName}</strong>
              {vorschau.ziel.email && <> ({vorschau.ziel.email})</>}
              {vorschau.ziel.hatAnmeldung ? ' – die Person mit Anmeldekonto.' : '.'}
            </p>

            <div className="overflow-x-auto">
              <table className="min-w-[560px] mt-3 w-full border-collapse text-[12.5px]">
                <thead>
                  <tr>
                    <th className="border-b border-line2 px-2 py-1.5 text-left text-[10.5px] font-semibold text-muted uppercase">
                      Zieht ein
                    </th>
                    <th className="border-b border-line2 px-2 py-1.5 text-right text-[10.5px] font-semibold text-muted uppercase">
                      Ereignisse
                    </th>
                    <th className="border-b border-line2 px-2 py-1.5 text-right text-[10.5px] font-semibold text-muted uppercase">
                      Aufgaben
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vorschau.quellen.map((q) => (
                    <tr key={q.id}>
                      <td className="border-b border-line px-2 py-1.5">
                        {q.displayName}
                        <span className="ml-1.5 text-[11px] text-muted">
                          {q.email ?? 'keine E-Mail'}
                        </span>
                      </td>
                      <td className="tabular border-b border-line px-2 py-1.5 text-right">
                        {q.ereignisse.toLocaleString('de-DE')}
                      </td>
                      <td className="tabular border-b border-line px-2 py-1.5 text-right">
                        {q.aufgaben}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="border-b border-line px-2 py-1.5 text-muted">
                      {vorschau.ziel.displayName} (bleibt)
                    </td>
                    <td className="tabular border-b border-line px-2 py-1.5 text-right text-muted">
                      {vorschau.ziel.ereignisse.toLocaleString('de-DE')}
                    </td>
                    <td className="tabular border-b border-line px-2 py-1.5 text-right text-muted">
                      {vorschau.ziel.aufgaben}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-3 rounded-lg bg-accent-soft px-2.5 py-2 text-[12.5px] text-accent-deep">
              <div className="font-semibold">Danach:</div>
              <ul className="mt-1 ml-4 list-disc">
                <li>
                  {vorschau.danach.ereignisse.toLocaleString('de-DE')} Ereignisse und{' '}
                  {vorschau.danach.aufgaben} Aufgaben an einer Person
                </li>
                <li>
                  Kennungen:{' '}
                  {vorschau.danach.kennungen.length === 0
                    ? 'keine externen'
                    : vorschau.danach.kennungen
                        .map(
                          // Herkunft einer *Person*: dazu gehört auch das
                          // Intranet, das keine Aufgaben liefert (Z-01).
                          (k) =>
                            `${SOURCE_LABELS[k.sourceId] ?? k.sourceId}${
                              k.username ? ` (${k.username})` : ''
                            }`,
                        )
                        .join(', ')}
                </li>
                <li>
                  E-Mail: {vorschau.danach.email ?? 'keine'} · Rolle: {vorschau.danach.rolle}
                </li>
              </ul>
            </div>

            <p className="mt-2.5 text-[11px] text-muted">
              <strong>Nicht rückgängig zu machen.</strong> Die überzähligen Personen werden
              gelöscht, nachdem ihre Ereignisse, Aufgaben und externen Kennungen umgehängt sind –
              alles in einer Transaktion. Die Kennungen müssen mit, sonst legt der nächste Import
              die Person erneut an und die Doppelung ist zurück.
            </p>

            <div className="mt-3.5 flex gap-2">
              <Button
                variant="primary"
                size="sm"
                disabled={merge.isPending}
                onClick={() => merge.mutate()}
              >
                {merge.isPending ? 'Führt zusammen …' : 'Zusammenführen'}
              </Button>
              <Button variant="ghost" size="sm" onClick={schliessen}>
                Abbrechen
              </Button>
            </div>
          </>
        )}

        {!vorschau && !isLoading && (
          <div className="mt-3.5">
            <Button variant="ghost" size="sm" onClick={schliessen}>
              Schließen
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
